// Payment Reviews — the B3 servicing-run review queue (Distribution Gate,
// B3 payout gate) at /portfolio-admin/payment-reviews. A recorded servicing
// run is HELD pending_review: its allocation is fully computed but not yet
// released for recognition / payout. B3 reviews each run here and approves it
// (releasing distribution) or rejects it (unwinding the recorded run). Writes
// gate on Payments.Review (B3 + ADMIN).

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  Textarea,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  SERVICING_PAYMENT_TYPES,
  type PendingServicingPaymentDto,
} from "@/api/portfolioAdmin/servicingApi";
import {
  useServicingPendingReview,
  useApproveServicingReview,
  useRejectServicingReview,
} from "@/hooks/portfolioAdmin/useServicing";

const VIEW_PERMISSION = "Payments.Review";

function paymentTypeLabel(type: string): string {
  return SERVICING_PAYMENT_TYPES.find((t) => t.value === type)?.label ?? type;
}

/** The PR-2 per-line B3 review screen for this run. */
function reviewHref(row: PendingServicingPaymentDto): string {
  return `/portfolio-admin/payment-reviews/${row.paymentId}`;
}

export default function ServicingReviewQueuePage() {
  const { tz } = useDateFormatter();
  const { hasPermission, hasRoleCode } = useAuth();
  const toast = useToast();
  const pending = useServicingPendingReview();
  const approve = useApproveServicingReview();
  const reject = useRejectServicingReview();

  const [rejectTarget, setRejectTarget] = useState<PendingServicingPaymentDto | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  if (!hasPermission(VIEW_PERMISSION) && !hasRoleCode("ADMIN")) {
    return (
      <div className="p-space-6">
        <EmptyState
          variant="empty"
          title="Not authorized"
          description="You don't have access to the payment review queue."
        />
      </div>
    );
  }

  const confirmApprove = async (row: PendingServicingPaymentDto) => {
    try {
      await approve.mutateAsync({ paymentId: row.paymentId, loanId: row.loanId });
      toast.success("Approved — the allocation is released for payout.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    try {
      await reject.mutateAsync({
        paymentId: rejectTarget.paymentId,
        loanId: rejectTarget.loanId,
        reason: rejectReason.trim(),
      });
      toast.success("Rejected — the recorded run was unwound.");
      setRejectTarget(null);
      setRejectReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const rows = pending.data ?? [];

  return (
    <div className="p-space-6 space-y-space-6">
      <PageHeader
        eyebrow="Finance"
        title="Payment Reviews"
        description="Recorded servicing runs waiting for your review before their allocation is released for payout."
      />

      <section>
        <h2 className="text-lg font-semibold text-onyx mb-space-3">
          Waiting for your review
        </h2>

        {pending.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : pending.isError ? (
          <EmptyState
            variant="empty"
            title="Couldn't load the review queue"
            description={extractErrorMessage(pending.error)}
            action={{ label: "Try again", onClick: () => void pending.refetch() }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="noResults"
            title="Nothing to review"
            description="Recorded payments awaiting review will appear here."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Borrower</TH>
                <TH>Type</TH>
                <TH>Amount</TH>
                <TH>Distributed</TH>
                <TH>Recorded</TH>
                <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.paymentId}>
                  <TD>
                    <Link
                      to={`/portfolio-admin/loans/${row.loanId}`}
                      className="text-gold hover:underline"
                    >
                      {row.loanNumber || "—"}
                    </Link>
                  </TD>
                  <TD>{row.borrowerName ?? "—"}</TD>
                  <TD>
                    <StatusBadge tone="warn">{paymentTypeLabel(row.paymentType)}</StatusBadge>
                  </TD>
                  <TD>
                    {formatCurrency(row.combinedAmount ?? row.amount)}
                    {(row.payAheadCount ?? 0) > 0 ? (
                      <span
                        className="block text-caption text-onyx/50"
                        data-testid={`pay-ahead-note-${row.paymentId}`}
                      >
                        incl. {row.payAheadCount} pay-ahead installment
                        {row.payAheadCount === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </TD>
                  <TD>
                    {formatCurrency(row.combinedTotalDistributed ?? row.totalDistributed)}
                    <span className="text-onyx/50"> · {row.lineCount} lines</span>
                  </TD>
                  <TD>
                    {formatDateInTz(row.recordedAt, tz)}
                    {row.recordedByUserName ? (
                      <span className="text-onyx/50"> · {row.recordedByUserName}</span>
                    ) : null}
                  </TD>
                  <TD>
                    <div className="flex items-center gap-space-2">
                      <Link to={reviewHref(row)} className="text-gold hover:underline">
                        Review lines
                      </Link>
                      <Button
                        size="sm"
                        onClick={() => void confirmApprove(row)}
                        disabled={approve.isPending}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setRejectTarget(row);
                          setRejectReason("");
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>

      <Modal
        open={rejectTarget != null}
        onClose={() => setRejectTarget(null)}
        title="Reject this recorded payment?"
      >
        <div className="space-y-space-4">
          <p className="text-sm text-onyx/70">
            Rejecting unwinds the recorded run — its disbursements and obligation
            balances are restored. Nothing has been distributed, so there's no
            claw-back. A reason is required.
          </p>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Why is this being rejected?"
            rows={3}
          />
          <div className="flex justify-end gap-space-2">
            <Button variant="secondary" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirmReject()}
              disabled={reject.isPending || rejectReason.trim().length === 0}
            >
              {reject.isPending ? "Rejecting…" : "Reject and unwind"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
